/**
 * QuoteCardActions — CRM-QUOTE-PIPELINE parity actions on the card drawer.
 *
 * Rendered ONLY for crm-quote cards (the unified Kanban renders quotes
 * mirrored from report.Quote). It re-adds the two affordances the bespoke
 * CRM pipeline had, now backed by the shared board:
 *
 *   Mark Won / Mark Lost — moves the card to the QUOTE_AWARDED / QUOTE_LOST
 *     lane via the drawer's existing `onMove`; the BE move handler then runs
 *     `quote_writeback.apply_card_move_to_quote`, writing quote_status back
 *     to report.Quote. Each button is presence-gated on the lane existing
 *     and hidden when the card is already in that lane.
 *
 *   Convert to Job — POSTs to /api/report/quote/{id}/convert/ via
 *     `api.convertQuoteToJob`; feature-detected so older hosts degrade to
 *     hiding the button rather than crashing.
 *
 * Pure-ish display + thin handlers: won/lost delegate state to the parent
 * (CAS-safe move handler); only the convert call owns local busy/error.
 */
import { useState } from 'react'
import type { MouseEvent } from 'react'
import { Briefcase, Loader2, Trophy, XCircle } from 'lucide-react'

import type { Card, Lane, QuoteConvertResult } from '../types'
import type { KanbanApi } from '../api'

const CRM_QUOTE_PROVIDERS = new Set(['crm-quote'])

/** I→O: is this card a crm-quote card with a linked quote? */
export function isQuoteCard(card: Pick<Card, 'provider' | 'external_provider' | 'quote_id'>): boolean {
  const provider = card.provider ?? card.external_provider ?? ''
  const ext = card.external_provider ?? ''
  return (CRM_QUOTE_PROVIDERS.has(provider) || CRM_QUOTE_PROVIDERS.has(ext)) && !!card.quote_id
}

interface QuoteCardActionsProps {
  card: Card
  api: KanbanApi
  /** The card's board lanes — used to resolve the won/lost lane ids. */
  lanes: Lane[]
  /** Drawer's CAS-safe lane move handler; we reuse it for won/lost. */
  onMove: (laneId: string) => void | Promise<void>
  /** Fired after a successful convert so the host can refetch / toast. */
  onConverted?: (result: QuoteConvertResult) => void
  /** True when the parent is mid-move (disables won/lost to avoid races). */
  moveBusy?: boolean
}

export function QuoteCardActions({
  card,
  api,
  lanes,
  onMove,
  onConverted,
  moveBusy = false,
}: QuoteCardActionsProps) {
  const [converting, setConverting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isQuoteCard(card)) return null

  const awardedLane = lanes.find((l) => l.kind === 'QUOTE_AWARDED')
  const lostLane = lanes.find((l) => l.kind === 'QUOTE_LOST')
  const canWin = !!awardedLane && card.lane !== awardedLane.id
  const canLose = !!lostLane && card.lane !== lostLane.id
  const canConvert = typeof api.convertQuoteToJob === 'function' && !!card.quote_id

  if (!canWin && !canLose && !canConvert) return null

  const stop = (e: MouseEvent) => e.stopPropagation()

  const handleWin = (e: MouseEvent) => {
    stop(e)
    if (awardedLane) void onMove(awardedLane.id)
  }

  const handleLose = (e: MouseEvent) => {
    stop(e)
    if (!lostLane) return
    // Lost is destructive-ish (writes quote_status='lost'); confirm so a
    // mis-click doesn't silently flip a live quote.
    if (typeof window !== 'undefined' && !window.confirm('Mark this quote as Lost?')) return
    void onMove(lostLane.id)
  }

  const handleConvert = async (e: MouseEvent) => {
    stop(e)
    if (!card.quote_id) return
    const convert = api.convertQuoteToJob
    if (typeof convert !== 'function') return
    if (typeof window !== 'undefined' && !window.confirm('Convert this quote to a Job?')) return
    setConverting(true)
    setError(null)
    try {
      const result = await convert(card.quote_id)
      onConverted?.(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Convert to job failed')
    } finally {
      setConverting(false)
    }
  }

  return (
    <div
      data-testid="quote-card-actions"
      className="flex flex-wrap items-center gap-2 border-t border-neutral-100 dark:border-zinc-700/70 pt-3"
    >
      {canWin && (
        <button
          type="button"
          data-testid="quote-action-win"
          onClick={handleWin}
          disabled={moveBusy || converting}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/40"
          title="Move to Awarded — marks the quote Won"
        >
          <Trophy className="h-3.5 w-3.5" aria-hidden />
          Mark Won
        </button>
      )}

      {canLose && (
        <button
          type="button"
          data-testid="quote-action-lose"
          onClick={handleLose}
          disabled={moveBusy || converting}
          className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 disabled:opacity-50 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-900/40"
          title="Move to Lost — marks the quote Lost"
        >
          <XCircle className="h-3.5 w-3.5" aria-hidden />
          Mark Lost
        </button>
      )}

      {canConvert && (
        <button
          type="button"
          data-testid="quote-action-convert"
          onClick={handleConvert}
          disabled={converting || moveBusy}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/40"
          title="Create a Job from this quote"
        >
          {converting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Briefcase className="h-3.5 w-3.5" aria-hidden />
          )}
          {converting ? 'Converting…' : 'Convert to Job'}
        </button>
      )}

      {error && (
        <span data-testid="quote-action-error" className="text-xs text-red-600 dark:text-red-400">
          {error}
        </span>
      )}
    </div>
  )
}
